import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { usePeriod, periodLabel } from '@/app/PeriodContext';
import { saldoHint } from '@/lib/balanceHint';
import { formatMoney, isNegative, formatDate } from '@/lib/money';
import type { MoneyString } from '@/lib/money';
import type { DashboardData } from './types';
import type { Entity } from '@/features/auth/AuthContext';

export function DashboardPage({
  selectedIds,
  entities,
}: {
  selectedIds: number[];
  entities: Entity[];
}) {
  const { period } = usePeriod();

  const query = useQuery({
    // selectedIds e periodo na chave: trocar qualquer um dos dois refaz a
    // consulta em vez de exibir o numero do recorte anterior.
    queryKey: ['dashboard', selectedIds, period.from, period.to],
    queryFn: () =>
      api.get<{ data: DashboardData }>(`/dashboard?from=${period.from}&to=${period.to}`, {
        entityIds: selectedIds.length > 0 ? selectedIds : undefined,
      }),
    select: (res) => res.data,
  });

  if (query.isPending) return <DashboardSkeleton />;

  if (query.isError) {
    return (
      <Panel tone="error">
        <p className="font-medium">Não foi possível carregar os indicadores.</p>
        <p className="mt-1 text-sm opacity-80">
          {query.error instanceof Error ? query.error.message : 'Erro desconhecido.'}
        </p>
        <button
          onClick={() => void query.refetch()}
          className="mt-3 rounded-lg bg-rose-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-rose-700"
        >
          Tentar de novo
        </button>
      </Panel>
    );
  }

  const d = query.data;
  const scope =
    selectedIds.length === 0
      ? `${entities.length} entidade${entities.length === 1 ? '' : 's'} · consolidado`
      : selectedIds
          .map((id) => entities.find((e) => e.id === id)?.name)
          .filter(Boolean)
          .join(' · ');

  return (
    <div className="mx-auto max-w-6xl">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h1 className="text-xl font-semibold tracking-tight">Dashboard</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {periodLabel(period)} · {scope}
        </p>
      </div>

      {/* ---------- Atenção agora ---------- */}
      {(d.payables.overdue_count > 0 || d.receivables.overdue_count > 0) && (
        <Panel tone="warn" className="mt-5">
          <p className="text-sm font-semibold">Atenção agora</p>
          <ul className="mt-2 space-y-1 text-sm">
            {d.payables.overdue_count > 0 && (
              <li>
                {d.payables.overdue_count} conta{d.payables.overdue_count > 1 ? 's' : ''} a pagar
                vencida{d.payables.overdue_count > 1 ? 's' : ''} —{' '}
                <strong>{formatMoney(d.payables.overdue)}</strong>
              </li>
            )}
            {d.receivables.overdue_count > 0 && (
              <li>
                {d.receivables.overdue_count} recebimento{d.receivables.overdue_count > 1 ? 's' : ''}{' '}
                em atraso — <strong>{formatMoney(d.receivables.overdue)}</strong>
              </li>
            )}
          </ul>
        </Panel>
      )}


      {/* ---------- KPIs ---------- */}
      <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-6">
        {/**
          * Saldo E disponível no mesmo card — RN-82.
          *
          * Para quem está negativo, "quanto eu tenho" e "quanto ainda posso
          * gastar" são perguntas diferentes, e a distância entre as duas é a
          * informação que importa. O total disponível já vinha calculado da API
          * e nenhuma tela mostrava.
          */}
        <Kpi label="Saldo total" value={d.balance.total} hint={saldoHint(d.balance)} signed />
        {/**
          * SÓ O QUE ESTÁ NO FILTRO.
          *
          * A legenda trazia também "R$ 257.800,00 no total" — a soma de tudo em
          * aberto, sem recorte de data, incluindo as parcelas dos anos
          * seguintes. Num painel cujo período diz "Este mês", é um número que
          * responde uma pergunta que ninguém fez, e o tamanho dele (seis dígitos
          * contra cinco) puxava a atenção para longe do que estava sendo pedido.
          *
          * Mesmo relato que já tinha vindo sobre a tela A pagar: "tem essa
          * posição que não segue o filtro, não acho interessante".
          */}
        <Kpi
          label="A receber"
          value={d.receivables.in_period}
          hint={`${d.receivables.in_period_count} no período`}
          tone="receita"
        />
        <Kpi
          label="A pagar"
          value={d.payables.in_period}
          hint={`${d.payables.in_period_count} no período`}
          tone="despesa"
        />
        <Kpi label="Receita do período" value={d.month.income} tone="receita" />
        <Kpi label="Despesa do período" value={d.month.expense} tone="despesa" />
        <Kpi label="Resultado do período" value={d.month.result} signed hint="por competência" />
      </div>

      {/* ---------- Contas ---------- */}
      <Section title="Contas bancárias">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {d.accounts.map((a) => (
            <div
              key={a.id}
              className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{a.name}</p>
                  <p className="truncate text-xs text-slate-500 dark:text-slate-400">
                    {a.entity_name} · {a.type}
                  </p>
                </div>
                <span
                  className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ background: a.color ?? '#94a3b8' }}
                />
              </div>
              <p
                className={`mt-3 text-lg font-semibold ${
                  isNegative(a.balance) ? 'text-rose-600 dark:text-rose-400' : ''
                }`}
              >
                {formatMoney(a.balance)}
              </p>
              {a.overdraft_limit !== '0.0000' && (
                <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                  disponível {formatMoney(a.available_balance)} · limite {formatMoney(a.overdraft_limit)}
                </p>
              )}
            </div>
          ))}
        </div>
      </Section>

      {/* ---------- Cartões ---------- */}
      {d.cards.cards.length > 0 && (
        <Section
          title="Cartões de crédito"
          aside={`${formatMoney(d.cards.available)} de limite disponível`}
        >
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {d.cards.cards.map((c) => (
              <div
                key={c.id}
                className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{c.name}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {c.last4 ? `•••• ${c.last4} · ` : ''}fecha dia {c.closing_day} · vence dia {c.due_day}
                    </p>
                  </div>
                  <span
                    className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{ background: c.color ?? '#94a3b8' }}
                  />
                </div>

                <p className="mt-3 text-lg font-semibold">{formatMoney(c.used)}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  de {formatMoney(c.credit_limit)}
                </p>

                <div className="mt-2.5 h-1.5 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                  <div
                    className={`h-full rounded-full ${
                      c.usage_percent >= 90
                        ? 'bg-rose-500'
                        : c.usage_percent >= 70
                          ? 'bg-amber-500'
                          : 'bg-indigo-500'
                    }`}
                    style={{ width: `${Math.min(c.usage_percent, 100)}%` }}
                  />
                </div>
                <p className="mt-1 text-right text-[11px] text-slate-400">{c.usage_percent}% usado</p>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Quais números seguem o período e quais não seguem. Sem esta linha,
          escolher "mês passado" e ver o saldo de hoje parece um erro. */}
      <p className="mt-8 text-xs text-slate-400 dark:text-slate-600">
        Período de {formatDate(d.period.start)} a {formatDate(d.period.end)} · receita, despesa e
        resultado por competência; a pagar e a receber por data de vencimento · o SALDO é a posição
        de hoje e não segue o período · o aviso de atraso no topo soma títulos vencidos de qualquer
        data · saldos calculados a partir dos movimentos, não de coluna armazenada
      </p>
    </div>
  );
}

// -------------------------------------------------------------------

function Kpi({
  label,
  value,
  hint,
  tone,
  signed,
}: {
  label: string;
  value: MoneyString;
  hint?: string;
  tone?: 'receita' | 'despesa';
  signed?: boolean;
}) {
  // Cor NUNCA é o único indicador (RNF-26): o rótulo já diz o que é o número.
  const color = signed
    ? isNegative(value)
      ? 'text-rose-600 dark:text-rose-400'
      : 'text-emerald-600 dark:text-emerald-400'
    : tone === 'receita'
      ? 'text-emerald-700 dark:text-emerald-400'
      : tone === 'despesa'
        ? 'text-rose-700 dark:text-rose-400'
        : '';

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3.5 dark:border-slate-800 dark:bg-slate-900">
      <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </p>
      <p className={`mt-1.5 text-base font-semibold ${color}`}>{formatMoney(value)}</p>
      {hint && <p className="mt-0.5 text-[11px] text-slate-400 dark:text-slate-500">{hint}</p>}
    </div>
  );
}

function Section({
  title,
  aside,
  children,
}: {
  title: string;
  aside?: string;
  children: React.ReactNode;
}) {
  return (
    <>
      <div className="mt-8 mb-3 flex items-baseline justify-between">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-400">{title}</h2>
        {aside && <span className="text-xs text-slate-400">{aside}</span>}
      </div>
      {children}
    </>
  );
}

function Panel({
  tone,
  className = '',
  children,
}: {
  tone: 'warn' | 'error';
  className?: string;
  children: React.ReactNode;
}) {
  const styles =
    tone === 'warn'
      ? 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200'
      : 'border-rose-200 bg-rose-50 text-rose-900 dark:border-rose-900/50 dark:bg-rose-950/30 dark:text-rose-200';

  return <div className={`rounded-xl border p-4 ${styles} ${className}`}>{children}</div>;
}

/** Skeleton com a forma do conteúdo real, não spinner solto. */
function DashboardSkeleton() {
  return (
    <div className="mx-auto max-w-6xl animate-pulse">
      <div className="h-6 w-32 rounded bg-slate-200 dark:bg-slate-800" />
      <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-6">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-20 rounded-xl bg-slate-200 dark:bg-slate-800" />
        ))}
      </div>
      <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-28 rounded-xl bg-slate-200 dark:bg-slate-800" />
        ))}
      </div>
    </div>
  );
}
